/* ==========================================================================
   Cart drawer — Indian Flower (Figma redesign)
   Footer tools (Order Note / Estimate Shipping / Coupon) + savings animation.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- Savings bar: animate fill on (re)render ---------------------------- */
  if (!customElements.get('cart-savings-bar')) {
    customElements.define(
      'cart-savings-bar',
      class CartSavingsBar extends HTMLElement {
        connectedCallback() {
          const fill = this.querySelector('.cart-savings__fill');
          if (!fill) return;
          const target = fill.style.width;
          fill.style.width = '0%';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              fill.style.width = target;
            });
          });
        }
      }
    );
  }

  /* ---- Footer tools ------------------------------------------------------- */
  if (!customElements.get('cart-drawer-tools')) {
    customElements.define(
      'cart-drawer-tools',
      class CartDrawerTools extends HTMLElement {
        connectedCallback() {
          this.pills = Array.from(this.querySelectorAll('.cart-tools__pill'));
          this.pills.forEach((pill) => {
            pill.addEventListener('click', () => this.toggle(pill));
          });
          this.initShipping();
          this.initCoupon();
        }

        panelFor(name) {
          return this.querySelector(`.cart-tools__panel[data-panel="${name}"]`);
        }

        toggle(pill) {
          const name = pill.dataset.tool;
          const isOpen = pill.getAttribute('aria-expanded') === 'true';

          this.pills.forEach((p) => {
            const open = p === pill && !isOpen;
            p.setAttribute('aria-expanded', open ? 'true' : 'false');
            const panel = this.panelFor(p.dataset.tool);
            if (panel) panel.hidden = !open;
          });

          if (!isOpen) {
            const panel = this.panelFor(name);
            if (panel) {
              const field = panel.querySelector('textarea, input, select');
              if (field) setTimeout(() => field.focus(), 50);
            }
          }
        }

        /* ---- Shipping estimator --------------------------------------------- */
        initShipping() {
          const root = this.querySelector('.cart-tools__shipping');
          if (!root) return;

          this.shipRoot = root;
          this.countrySelect = root.querySelector('[data-ship-country]');
          this.provinceSelect = root.querySelector('[data-ship-province]');
          this.provinceWrapper = root.querySelector('[data-ship-province-wrapper]');
          this.zipInput = root.querySelector('[data-ship-zip]');
          this.shipResults = root.querySelector('[data-ship-results]');
          const submit = root.querySelector('[data-ship-submit]');

          if (this.countrySelect) {
            this.countrySelect.addEventListener('change', () => this.populateProvinces());
            this.populateProvinces();
          }
          if (submit) submit.addEventListener('click', () => this.estimate());
        }

        populateProvinces() {
          if (!this.countrySelect || !this.provinceSelect) return;
          const option = this.countrySelect.options[this.countrySelect.selectedIndex];
          let provinces = [];
          try {
            provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
          } catch (e) {
            provinces = [];
          }
          this.provinceSelect.innerHTML = '';
          if (!provinces.length) {
            if (this.provinceWrapper) this.provinceWrapper.hidden = true;
            return;
          }
          provinces.forEach((p) => {
            const opt = document.createElement('option');
            opt.value = p[0];
            opt.textContent = p[1];
            this.provinceSelect.appendChild(opt);
          });
          if (this.provinceWrapper) this.provinceWrapper.hidden = false;
        }

        estimate() {
          if (!this.shipResults) return;
          const country = this.countrySelect ? this.countrySelect.value : '';
          const province =
            this.provinceWrapper && !this.provinceWrapper.hidden && this.provinceSelect
              ? this.provinceSelect.value
              : '';
          const zip = this.zipInput ? this.zipInput.value.trim() : '';

          this.shipResults.className = 'cart-tools__results';
          this.shipResults.textContent = 'Calculating…';

          const params = new URLSearchParams();
          if (zip) params.append('shipping_address[zip]', zip);
          if (country) params.append('shipping_address[country]', country);
          if (province) params.append('shipping_address[province]', province);

          fetch(`${window.Shopify && Shopify.routes ? Shopify.routes.root : '/'}cart/shipping_rates.json?${params.toString()}`)
            .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
            .then(({ ok, data }) => {
              const rates = data && data.shipping_rates;
              if (!ok || !rates) {
                this.shipResults.className = 'cart-tools__results cart-tools__results--error';
                this.shipResults.textContent =
                  'We could not estimate shipping for this address. Please check the details.';
                return;
              }
              if (!rates.length) {
                this.shipResults.className = 'cart-tools__results';
                this.shipResults.textContent = 'No shipping rates available for this address.';
                return;
              }
              const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
              const list = document.createElement('ul');
              rates.forEach((rate) => {
                const li = document.createElement('li');
                const name = document.createElement('span');
                name.textContent = rate.presentment_name || rate.name;
                const price = document.createElement('strong');
                const value = parseFloat(rate.price) === 0 ? 'Free' : `${cur} ${rate.price}`.trim();
                price.textContent = value;
                li.append(name, price);
                list.appendChild(li);
              });
              this.shipResults.className = 'cart-tools__results';
              this.shipResults.innerHTML = '';
              this.shipResults.appendChild(list);
            })
            .catch(() => {
              this.shipResults.className = 'cart-tools__results cart-tools__results--error';
              this.shipResults.textContent = 'Something went wrong. Please try again.';
            });
        }

        /* ---- Coupon --------------------------------------------------------- */
        initCoupon() {
          const root = this.querySelector('.cart-tools__coupon');
          if (!root) return;
          const input = root.querySelector('[data-coupon-input]');
          const submit = root.querySelector('[data-coupon-submit]');
          const results = root.querySelector('[data-coupon-results]');
          if (!input || !submit) return;

          const apply = () => {
            const code = input.value.trim();
            if (!code) {
              input.focus();
              return;
            }
            results.className = 'cart-tools__results';
            results.textContent = 'Applying…';
            const shopRoot = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
            fetch(`${shopRoot}discount/${encodeURIComponent(code)}`, { redirect: 'manual' })
              .then(() => {
                results.className = 'cart-tools__results cart-tools__results--success';
                results.textContent = `“${code}” saved — your discount is applied at checkout.`;
              })
              .catch(() => {
                results.className = 'cart-tools__results cart-tools__results--error';
                results.textContent = 'We could not apply that code. Please check and try again.';
              });
          };

          submit.addEventListener('click', apply);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
          });
        }
      }
    );
  }
})();
